import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';

const FeedbackModal = ({ isOpen, onClose, onSubmit, messageId, userQuestion, voteType = 'down', context = 'chat' }) => {
  const [feedback, setFeedback] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const negativeFeedbackCategories = context === 'ai-search' ? [
    'Incorrect or inaccurate information',
    'Query didn\'t understand my question',
    'Missing relevant documents/sources',
    'Response was too generic',
    'Technical error occurred',
    'Poor document search results',
    'Incomplete answer',
    'Other'
  ] : [
    'Incorrect or inaccurate information',
    'Query didn\'t understand my question',
    'Generated wrong SQL query',
    'Missing important data',
    'Response was too generic',
    'Technical error occurred',
    'Other'
  ];

  const positiveFeedbackCategories = context === 'ai-search' ? [
    'Accurate and helpful information',
    'Understood my question perfectly',
    'Found relevant documents/sources',
    'Provided comprehensive answer',
    'Response was specific and actionable',
    'Fast and efficient search',
    'Great document retrieval',
    'Other'
  ] : [
    'Accurate and helpful information',
    'Understood my question perfectly',
    'Generated correct SQL query',
    'Provided comprehensive data',
    'Response was specific and actionable',
    'Fast and efficient response',
    'Great visualization/charts',
    'Other'
  ];

  const feedbackCategories = voteType === 'up' ? positiveFeedbackCategories : negativeFeedbackCategories;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        messageId,
        userQuestion,
        category,
        feedback: feedback.trim() || null,
        voteType,
        timestamp: new Date().toISOString()
      });
      
      // Reset form
      setFeedback('');
      setCategory('');
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className={`h-5 w-5 ${voteType === 'up' ? 'text-green-500' : 'text-red-500'}`} />
              <h3 className="text-lg font-semibold text-gray-900">
                {voteType === 'up' ? 'What did you like?' : 'Help us improve'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {voteType === 'up' ? 'What did you like?' : 'What went wrong?'}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select a category</option>
                {feedbackCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional details (optional)
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={voteType === 'up' ? "Please provide more specific details about what you liked..." : "Please provide more specific details about what went wrong..."}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!category || isSubmitting}
                className={`flex-1 px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  voteType === 'up' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal; 